/**
 * domains/market/engines/setup2/types.ts — MER Algo Footprints v7.1H
 *
 * Production-grade types for the Setup Engine v2.
 * These are the authoritative typed contract — consumed by Thesis Engine,
 * Ranking Engine, Journal, and Alert system.
 *
 * Design principles:
 *   - String literal enums for JSON safety
 *   - Every scored component is separately named and explainable
 *   - Hard disqualifiers produce named reasons, never silent suppression
 *   - Audit trails capture every scoring decision
 */

// ─── Core enums ───────────────────────────────────────────────────────────────

export type SetupClass =
  | 'CONTINUATION'
  | 'REVERSAL'
  | 'TRAP_REVERSAL'
  | 'LIQUIDITY_SWEEP'
  | 'BREAKOUT_CONFIRMATION'
  | 'MEAN_REVERSION'
  | 'NO_VALID_SETUP';

export type SetupState =
  | 'DETECTED'      // candidate identified but not scored
  | 'QUALIFIED'     // passed initial screening
  | 'DEVELOPING'    // direction clear, confirmation pending
  | 'READY'         // all conditions met, actionable
  | 'ACTIVE'        // position entered (external tracking)
  | 'INVALIDATED'   // structural level broken
  | 'EXPIRED';      // time-based expiry

export type SetupDirection =
  | 'LONG'
  | 'SHORT'
  | 'NEUTRAL';

// ─── Scoring breakdown ────────────────────────────────────────────────────────

export interface QualityBreakdown {
  structure_score:          number;   // 0–100 — BOS/CHOCH alignment, structure clarity
  context_score:            number;   // 0–100 — regime, trend, phase alignment
  liquidity_score:          number;   // 0–100 — zone proximity, sweep quality, FVG
  trigger_score:            number;   // 0–100 — entry trigger specificity and freshness
  risk_reward_score:        number;   // 0–100 — R/R quality and stop placement
  execution_quality_score:  number;   // 0–100 — timing, zone, displacement freshness
  raw_composite:            number;   // 0–100 before penalties
  total_penalty:            number;   // ≥ 0 subtracted from raw
  setup_score:              number;   // 0–100 final (raw_composite − total_penalty, floor 0)
}

export interface ReadinessBreakdown {
  trigger_confirmed:       boolean;
  confirmation_present:    boolean;
  execution_zone_active:   boolean;
  invalidation_clear:      boolean;
  rr_viable:               boolean;
  timing_quality:          'good' | 'marginal' | 'poor';
  readiness_score:         number;   // 0–100
}

// ─── Penalty and disqualifier ─────────────────────────────────────────────────

export interface Penalty {
  reason:  string;
  points:  number;   // > 0 — subtracted from raw_composite
}

export interface Disqualifier {
  rule:    string;   // human-readable rule name
  reason:  string;   // specific reason for this setup
  fatal:   boolean;  // true = setup cannot be READY regardless of score
}

// ─── Entry / risk / target model ─────────────────────────────────────────────

export interface SetupEntryModel {
  trigger:             string;          // specific price or event
  entry_zone_low:      number | null;   // price range for limit entries
  entry_zone_high:     number | null;
  entry_price_ideal:   number | null;   // midpoint / ideal entry
  confirmation:        string;          // observable confirmation required
  timing_note:         string | null;
}

export interface SetupRiskModel {
  stop_level:          number | null;   // hard stop price
  stop_basis:          string;          // why this stop (e.g. 'Below BOS level')
  invalidation_event:  string;          // event or price that kills the thesis
  stop_quality:        number;          // 0–100
  stop_note:           string | null;
}

export interface SetupTargets {
  target_1:      number | null;
  target_2:      number | null;
  risk_reward:   number | null;   // computed (target_1 - entry) / (entry - stop)
  target_basis:  string;          // rationale (e.g. 'Next resistance zone')
}

// ─── Actionability summary ────────────────────────────────────────────────────

export interface SetupActionability {
  is_actionable:       boolean;
  can_become_ready:    boolean;   // true if disqualifiers are soft / resolvable
  blocking_disqs:      string[];  // fatal disqualifier rules blocking READY
  missing_items:       string[];  // what would upgrade state to READY
}

// ─── Audit ────────────────────────────────────────────────────────────────────

export interface ThresholdSnapshot {
  min_setup_score:     number;
  min_readiness_score: number;
  min_rr:              number;
  max_trap_severity:   string;
}

export const DEFAULT_THRESHOLDS: ThresholdSnapshot = {
  min_setup_score:     65,
  min_readiness_score: 70,
  min_rr:              1.2,
  max_trap_severity:   'Medium',
};

export interface DetectionTrace {
  rule_id:      string;    // e.g. 'BOS_CONTINUATION', 'CHOCH_REVERSAL'
  rule_fired:   boolean;
  conditions:   string[];  // what conditions were checked
  reason:       string;    // why this family was selected
}

export interface StateTransition {
  state:  SetupState;
  reason: string;  // deterministic reason for this state
}

export interface SetupAudit {
  setup_id:           string;
  detected_at:        number;        // Unix ms
  engine_version:     string;
  scoring_version:    string;        // SCORING_VERSION constant
  schema_version:     string;        // SCHEMA_VERSION constant
  input_hash:         string;        // djb2 of serialized normalized inputs
  scoring_weights:    ScoringWeights;
  threshold_snapshot: ThresholdSnapshot;
  signal_inputs:      Record<string, unknown>;
  detection_trace:    DetectionTrace[];
  state_history:      SetupState[];  // simplified state sequence for quick reads
  state_transitions:  StateTransition[];  // full transition log with reasons
}

// ─── Scoring weights (configurable, always explicit) ─────────────────────────

export interface ScoringWeights {
  structure:         number;   // default 0.25
  context:           number;   // default 0.20
  liquidity:         number;   // default 0.15
  trigger:           number;   // default 0.15
  risk_reward:       number;   // default 0.15
  execution_quality: number;   // default 0.10
}

export const DEFAULT_SCORING_WEIGHTS: ScoringWeights = {
  structure:         0.25,
  context:           0.20,
  liquidity:         0.15,
  trigger:           0.15,
  risk_reward:       0.15,
  execution_quality: 0.10,
};

// ─── The master setup object ──────────────────────────────────────────────────

export interface RichSetup {
  // Identity
  setup_id:             string;
  symbol:               string;
  timeframe:            string;
  timestamp:            number;

  // Classification
  setup_classification: SetupClass;
  setup_name:           string;     // human-readable variant name
  setup_state:          SetupState;
  direction:            SetupDirection;

  // Scores
  setup_score:          number;     // 0–100 final weighted
  readiness_score:      number;     // 0–100 independent readiness

  // Breakdown
  quality_breakdown:    QualityBreakdown;
  readiness_breakdown:  ReadinessBreakdown;

  // Signals
  detected_signals:     string[];   // raw signals that triggered detection
  supporting_factors:   string[];   // top supporting factors
  conflicting_factors:  string[];   // active conflicts / headwinds

  // Penalties and disqualifiers
  penalties:      Penalty[];
  disqualifiers:  Disqualifier[];

  // Plans
  entry_model:    SetupEntryModel;
  risk_model:     SetupRiskModel;
  targets:        SetupTargets;

  // Actionability
  actionability:  SetupActionability;

  // Audit
  audit:          SetupAudit;
}

// ─── Input type — normalized engine outputs ───────────────────────────────────

export interface SetupEngineInput {
  symbol:    string;
  timeframe: string;
  candles:   import('../../../../shared/types').Candle[];
  smc:       import('../../../../shared/types').SMCResult | null;
  regime:    import('../../../../shared/types').RegimeResult | null;
  traps:     import('../../../../shared/types').TrapSignal[];
  zones:     import('../../../../shared/types').LiquidityZone[];
  trend:     import('../../../../shared/types').TrendResult | null;
  gamma:     import('../../../../shared/types').GammaResult | null;
  curPrice:  number;
  weights?:  Partial<ScoringWeights>;  // optional override
}
